import type { Range } from 'xlsx';

export type TableExportColumn = {
  dataKey: string;
  label: string;
  parentLabel?: string;
};

const NON_EXPORTABLE_COLUMN_TYPES = new Set(['sparkline', 'stackedhorizontalbar', 'posnegbarchart']);

const INTERNAL_ROW_KEY_PATTERN = /(?:Color|Max|Min|Width|ShowText|_Components|__series|__url)$/;

export const normalizeTableExportHeaderLabel = (value: string | undefined | null): string =>
  String(value ?? '')
    .replace(/\u05bf/g, '')
    .replace(/\n/g, ' ')
    .replace(/_/g, ' ')
    .trim();

export const isNonExportableTableColumnType = (dataType?: string): boolean => {
  const normalized = dataType?.trim().toLowerCase() ?? '';
  return NON_EXPORTABLE_COLUMN_TYPES.has(normalized);
};

export const isInternalTableRowKey = (key: string): boolean => {
  if (!key) return true;
  if (key === 'id' || key === 'order' || key === 'description') return true;
  return INTERNAL_ROW_KEY_PATTERN.test(key);
};

export const extractTableExportCellValue = (value: unknown): string | number => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object' && value !== null && 'text' in value) {
    return String((value as { text?: unknown }).text ?? '');
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return String(value);
};

export const buildTableExportColumnsFromRowKeys = (rows: Record<string, unknown>[]): TableExportColumn[] => {
  if (!rows.length) {
    return [];
  }

  return Object.keys(rows[0])
    .filter(key => !isInternalTableRowKey(key) && key !== 'name')
    .map(key => ({
      dataKey: key,
      label: normalizeTableExportHeaderLabel(key),
    }));
};

const buildParentHeaderRow = (columns: TableExportColumn[]): string[] =>
  columns.map((column, index) => {
    const parentLabel = column.parentLabel;
    if (!parentLabel) {
      return '';
    }
    const previousParentLabel = index > 0 ? columns[index - 1].parentLabel : undefined;
    return previousParentLabel === parentLabel ? '' : parentLabel;
  });

const buildParentHeaderMerges = (columns: TableExportColumn[], headerRowIndex: number): Range[] => {
  const merges: Range[] = [];
  let start = 0;

  while (start < columns.length) {
    const parentLabel = columns[start].parentLabel;
    if (!parentLabel) {
      start += 1;
      continue;
    }

    let end = start + 1;
    while (end < columns.length && columns[end].parentLabel === parentLabel) {
      end += 1;
    }

    if (end - start > 1) {
      merges.push({
        s: { r: headerRowIndex, c: start },
        e: { r: headerRowIndex, c: end - 1 },
      });
    }

    start = end;
  }

  return merges;
};

export type TableExportSheet = {
  aoa: unknown[][];
  merges?: Range[];
  headerLabels: string[];
};

export const buildTableExportSheet = (
  rows: Record<string, unknown>[],
  columns: TableExportColumn[],
  options?: { includeParentHeaderRow?: boolean },
): TableExportSheet => {
  if (!columns.length) {
    return { aoa: [], headerLabels: [] };
  }

  const headerLabels = columns.map(column => column.label);
  const hasParentHeaderRow =
    Boolean(options?.includeParentHeaderRow) && columns.some(column => Boolean(column.parentLabel));
  const aoa: unknown[][] = [];

  if (hasParentHeaderRow) {
    const parentRow = buildParentHeaderRow(columns);
    aoa.push(parentRow);
    aoa.push(headerLabels);
  } else {
    aoa.push(headerLabels);
  }

  rows.forEach(row => {
    aoa.push(columns.map(column => extractTableExportCellValue(row[column.dataKey])));
  });

  const merges = hasParentHeaderRow ? buildParentHeaderMerges(columns, 0) : undefined;

  return { aoa, merges, headerLabels };
};

const formatTableExportCsvCell = (cell: unknown): string => {
  if (cell === null || cell === undefined) {
    return '';
  }
  if (typeof cell === 'number' && Number.isFinite(cell)) {
    return String(cell);
  }

  const text = String(cell);
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const formatTableExportCsv = (rows: unknown[][]): string =>
  rows.map(row => (Array.isArray(row) ? row.map(formatTableExportCsvCell).join(',') : '')).join('\n');

export const downloadTableExportCsv = (
  rows: Record<string, unknown>[],
  columns: TableExportColumn[],
  filename: string,
  options?: { includeParentHeaderRow?: boolean },
): void => {
  const sheet = buildTableExportSheet(rows, columns, options);
  const csv = `\uFEFF${formatTableExportCsv(sheet.aoa)}`;
  const link = document.createElement('a');
  link.download = `${filename}.csv`;
  link.style.display = 'none';

  if (typeof URL.createObjectURL === 'function') {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    link.href = url;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return;
  }

  link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};
